'use client';

import { useCallback, useState } from 'react';
import { AxiosError } from 'axios';
import { useAuthService } from './useAuthService';
import { AuthErrorCode } from '../../constants/auth';
import type { ApiErrorResponse } from '../../infrastructure/auth/utils/types';

type UseEmailVerificationParams = {
  phoneNumber: string;
  otpCode: string;
};

type EmailVerificationError = {
  errorCode: AuthErrorCode;
  message: string;
};

type VerifyEmailResult = {
  success: boolean;
  shouldRedirect: boolean;
  errorCode?: AuthErrorCode;
};

const SESSION_CRITICAL_ERROR_CODES = new Set([
  AuthErrorCode.MAX_ATTEMPTS_EXCEEDED,
  AuthErrorCode.SESSION_LOCKED,
  AuthErrorCode.SESSION_NOT_FOUND,
  AuthErrorCode.OTP_EXPIRED,
  AuthErrorCode.OTP_NOT_FOUND,
  AuthErrorCode.INVALID_OTP,
]);

type UseEmailVerificationReturn = {
  remainingAttempts: number | undefined;
  isVerifying: boolean;
  isVerified: boolean;
  isFailed: boolean;
  error: EmailVerificationError | null;
  verifyEmail: (selectedEmail: string) => Promise<VerifyEmailResult>;
  reset: () => void;
};

/**
 * Hook for verifying email ownership of existing users
 *
 * @description
 * Used when `candidateEmails` is returned after OTP verification.
 * When user selects their actual email, account is linked and login completes.
 *
 * ## Prerequisites
 * - After OTP verification in `useOTPAuth`
 * - `userType` is `customer` or `subscriber`
 * - `candidateEmails` exists
 *
 * ## candidateEmails Composition
 * - 1 real email + 2 decoy emails = 3 total
 * - User must select their own email for security verification
 *
 * ## Verification Results
 * - Correct selection → `isVerified: true` + WordPress session cookie set
 * - Wrong selection → `remainingAttempts` decreases (max 3 attempts)
 * - Attempts exhausted → `isFailed: true`
 *
 * @example
 * ```tsx
 * const { candidateEmails, otpCode, phoneNumber } = useOTPAuth({ phoneNumber: '+821012345678' });
 *
 * // Use only when candidateEmails exists
 * const { verifyEmail, isVerified, remainingAttempts } = useEmailVerification({
 *   phone: phoneNumber,
 *   otpCode: otpCode!,
 * });
 *
 * // Display candidateEmails in UI and let user select
 * const success = await verifyEmail(selectedEmail);
 *
 * if (isVerified) {
 *   // Login complete
 * }
 * ```
 */
export const useEmailVerification = ({
  phoneNumber,
  otpCode,
}: UseEmailVerificationParams): UseEmailVerificationReturn => {
  const { useVerifyEmail } = useAuthService();
  const { mutateAsync: verifyEmailMutation, isPending: isVerifying } = useVerifyEmail();

  const [remainingAttempts, setRemainingAttempts] = useState<number | undefined>(undefined);
  const [isVerified, setIsVerified] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [error, setError] = useState<EmailVerificationError | null>(null);

  const verifyEmail = useCallback(
    async (selectedEmail: string): Promise<VerifyEmailResult> => {
      if (!phoneNumber || !otpCode) {
        return { success: false, shouldRedirect: false };
      }

      setError(null);

      try {
        const response = await verifyEmailMutation({ phoneNumber, otpCode, selectedEmail });
        return handleVerifyEmailResponse(response);
      } catch (err) {
        return handleVerifyEmailError(err as AxiosError<ApiErrorResponse>);
      }
    },
    [phoneNumber, otpCode, verifyEmailMutation],
  );

  const handleVerifyEmailResponse = (response: Awaited<ReturnType<typeof verifyEmailMutation>>): VerifyEmailResult => {
    const { emailVerified, remainingAttempts: remaining } = response.data;

    if (response.success && emailVerified) {
      setIsVerified(true);
      return { success: true, shouldRedirect: false };
    }

    setRemainingAttempts(remaining);

    const isAttemptsExhausted = remaining === 0;
    if (isAttemptsExhausted) {
      setIsFailed(true);
    }

    return { success: false, shouldRedirect: isAttemptsExhausted };
  };

  const handleVerifyEmailError = (axiosError: AxiosError<ApiErrorResponse>): VerifyEmailResult => {
    const errorData = axiosError.response?.data;

    if (!errorData?.errorCode) {
      return { success: false, shouldRedirect: false };
    }

    const errorCode = errorData.errorCode as AuthErrorCode;
    const isSessionCriticalError = SESSION_CRITICAL_ERROR_CODES.has(errorCode);

    if (isSessionCriticalError) {
      setIsFailed(true);
    }

    setError({ errorCode, message: errorData.message });
    return { success: false, shouldRedirect: isSessionCriticalError, errorCode };
  };

  const reset = useCallback(() => {
    setRemainingAttempts(undefined);
    setIsVerified(false);
    setIsFailed(false);
    setError(null);
  }, []);

  return {
    remainingAttempts,
    isVerifying,
    isVerified,
    isFailed,
    error,
    verifyEmail,
    reset,
  };
};
